import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { IconPointFilled } from '@tabler/icons-react';

interface IBreadcrumbSegment {
  label: string;
  href: string;
  state?: Record<string, unknown>;
}

interface IBreadcrumbProps {
  path: IBreadcrumbSegment[];
  onNavigateRequest?: (href: string, state?: Record<string, unknown>) => void;
}

const BreadcrumbItem = ({
  children,
  href,
  state,
  active,
  onNavigate,
}: {
  children: ReactNode;
  href?: string;
  state?: Record<string, unknown>;
  active?: boolean;
  onNavigate?: (href: string, state?: Record<string, unknown>) => void;
}) => {
  return (
    <li className="flex items-center text-sm">
      {href && !active ? (
        <button
          type="button"
          className="text-neutral-secondary font-medium text-sm leading-[150%] cursor-pointer"
          onClick={() => onNavigate?.(href, state)}
        >
          {children}
        </button>
      ) : (
        <p className="text-sm font-semibold leading-[150%] text-neutral-primary">
          {children}
        </p>
      )}
      {!active && (
        <IconPointFilled size={14} className="text-neutral-pressed mx-2" />
      )}
    </li>
  );
};

const Breadcrumb = ({ path, onNavigateRequest }: IBreadcrumbProps) => {
  const navigate = useNavigate();

  const handleNavigate = (href: string, state?: Record<string, unknown>) => {
    if (onNavigateRequest) {
      onNavigateRequest(href, state);
    } else {
      navigate(href, { state });
    }
  };

  return (
    <nav className="flex items-center" aria-label="Breadcrumb">
      <ol className="inline-flex items-center">
        {path.map((segment, index) => (
          <BreadcrumbItem
            key={index}
            href={index < path.length - 1 ? segment.href : undefined}
            state={segment.state}
            active={index === path.length - 1}
            onNavigate={handleNavigate}
          >
            {segment.label}
          </BreadcrumbItem>
        ))}
      </ol>
    </nav>
  );
};

export { Breadcrumb, BreadcrumbItem };
